import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import API from '@/utils/api';
import {
  Users, TrendingUp, FileText, Mail, AlertTriangle,
  CreditCard, Eye, DollarSign, ChevronRight, Clock,
  UserCheck, ShieldAlert, ArrowLeft, Trash2, Loader2, UserX
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

function Sparkline({ data, color = '#f97316', height = 60, width = '100%' }) {
  if (!data || data.length === 0) return null;
  const values = data.map(d => d.count);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const svgW = 300;
  const svgH = height;
  const padding = 2;

  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (svgW - padding * 2);
    const y = svgH - padding - ((v - min) / range) * (svgH - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `${padding},${svgH - padding} ${points} ${svgW - padding},${svgH - padding}`;
  const total = values.reduce((a, b) => a + b, 0);

  return (
    <div className="relative" style={{ width, height: svgH }}>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill={`url(#grad-${color.replace('#','')})`} />
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {total === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] text-gray-600">Brak danych</span>
        </div>
      )}
    </div>
  );
}

function TrendCard({ title, data, color, colorClass, total, subLabel }) {
  return (
    <div className={`bg-gradient-to-br ${colorClass} border rounded-xl p-4 overflow-hidden`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-400">{title}</span>
        <span className="text-lg font-bold text-white">{total}</span>
      </div>
      {subLabel && <p className="text-[10px] text-gray-500 mb-2">{subLabel}</p>}
      <Sparkline data={data} color={color} height={50} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'orange' }) {
  const colorMap = {
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/20 text-orange-400',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/20 text-emerald-400',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/20 text-blue-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/20 text-purple-400',
    red: 'from-red-500/20 to-red-600/10 border-red-500/20 text-red-400',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/20 text-amber-400',
  };
  const c = colorMap[color] || colorMap.orange;
  return (
    <div className={`bg-gradient-to-br ${c} border rounded-xl p-4`} data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function Tab({ label, active, onClick, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
        active
          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
          : 'text-gray-400 hover:text-white hover:bg-white/5'
      }`}
    >
      {label} {count !== undefined && <span className="ml-1 text-xs opacity-60">({count})</span>}
    </button>
  );
}

/* ───── Delete User By Email ───── */
function DeleteUserByEmail() {
  const [email, setEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDelete = async () => {
    if (!email.trim()) return;
    if (!window.confirm(`Na pewno usunac konto: ${email}? Tej operacji nie mozna cofnac.`)) return;
    setDeleting(true);
    setResult(null);
    try {
      const res = await axios.delete(`${API}/admin/users/by-email/${encodeURIComponent(email.trim())}`, { headers: headers() });
      setResult({ ok: true, msg: `Usunieto: ${email}` });
      setEmail('');
    } catch (err) {
      setResult({ ok: false, msg: err.response?.data?.detail || 'Blad usuwania' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-8 bg-[#12121A] rounded-2xl border border-zinc-800 p-5" data-testid="delete-user-section">
      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
        <UserX className="w-4 h-4 text-red-400" /> Usun uzytkownika po emailu
      </h3>
      <p className="text-zinc-500 text-xs mb-3">Umozliwia ponowna rejestracje — usuwa konto i wszystkie dane klienta.</p>
      <div className="flex gap-2">
        <input
          type="email"
          placeholder="email@klienta.pl"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-orange-500"
          data-testid="delete-user-email-input"
        />
        <button
          onClick={handleDelete}
          disabled={deleting || !email.trim()}
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
          data-testid="delete-user-btn"
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Usun
        </button>
      </div>
      {result && (
        <p className={`mt-2 text-xs ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
          {result.msg}
        </p>
      )}
    </div>
  );
}

function DeleteKennelSection() {
  const [slug, setSlug] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDelete = async () => {
    if (!slug.trim()) return;
    if (!window.confirm(`Usunąć hodowlę "${slug}" i wszystkie powiązane mioty?`)) return;
    setDeleting(true);
    setResult(null);
    try {
      const res = await axios.delete(`${API}/admin/kennels/by-slug/${encodeURIComponent(slug.trim())}`, { headers: headers() });
      setResult({ ok: true, msg: res.data.message || 'Hodowla usunięta' });
      setSlug('');
    } catch (err) {
      setResult({ ok: false, msg: err.response?.data?.detail || 'Błąd usuwania' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4 bg-[#12121A] rounded-2xl border border-zinc-800 p-5" data-testid="delete-kennel-section">
      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
        <Trash2 className="w-4 h-4 text-red-400" /> Usuń hodowlę po slug
      </h3>
      <p className="text-zinc-500 text-xs mb-3">Usuwa hodowlę i wszystkie powiązane mioty. Slug to końcówka URL-a (np. "apis-hof-4").</p>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="slug-hodowli"
          value={slug}
          onChange={e => setSlug(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-orange-500"
          data-testid="delete-kennel-slug-input"
        />
        <button
          onClick={handleDelete}
          disabled={deleting || !slug.trim()}
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
          data-testid="delete-kennel-btn"
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Usuń
        </button>
      </div>
      {result && (
        <p className={`mt-2 text-xs ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
          {result.msg}
        </p>
      )}
    </div>
  );
}

function DeleteLitterBySlug() {
  const [slug, setSlug] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDelete = async () => {
    const s = slug.trim();
    if (!s || deleting) return;
    if (!window.confirm(`Usunąć miot "${s}"? Akcji nie można cofnąć.`)) return;
    setDeleting(true);
    setResult(null);
    try {
      const { data } = await axios.delete(`${API}/admin/litters/${encodeURIComponent(s)}`, { headers: headers() });
      setResult({ ok: true, msg: `✅ Usunięto: ${data.deleted_name || s}` });
      setSlug('');
    } catch (e) {
      setResult({ ok: false, msg: `❌ ${e?.response?.data?.detail || e.message}` });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4 bg-[#12121A] rounded-2xl border border-zinc-800 p-5" data-testid="delete-litter-section">
      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
        <Trash2 className="w-4 h-4 text-red-400" /> Usuń miot po slug
      </h3>
      <p className="text-zinc-500 text-xs mb-3">
        Usuwa pojedynczy miot. Slug = końcówka URL (np. z <code className="text-emerald-400">/miot/owczarek-niemiecki-miot-b-planowany</code> → wpisz <code className="text-emerald-400">owczarek-niemiecki-miot-b-planowany</code>).
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="slug-miotu"
          value={slug}
          onChange={e => setSlug(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-orange-500"
          data-testid="delete-litter-slug-input"
        />
        <button
          onClick={handleDelete}
          disabled={deleting || !slug.trim()}
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
          data-testid="delete-litter-btn"
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Usuń
        </button>
      </div>
      {result && (
        <p className={`mt-2 text-xs ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
          {result.msg}
        </p>
      )}
    </div>
  );
}

function SendSingleClaimMail() {
  const [collection, setCollection] = useState('kennels');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    const s = slug.trim();
    if (!s || loading) return;
    if (!window.confirm(`Wysłać mail z zaproszeniem dla "${s}"? Wyśle natychmiast (omija ramp-up i godziny pracy).`)) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast/send-single`,
        { collection, slug: s },
        { headers: headers(), timeout: 30000 }
      );
      setResult({ ok: true, msg: `✅ Wysłano na ${data.sent_to} (${data.entity_name})` });
      setSlug('');
    } catch (e) {
      setResult({ ok: false, msg: `❌ ${e?.response?.data?.detail || e.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 bg-[#12121A] rounded-2xl border border-zinc-800 p-5" data-testid="send-single-claim-section">
      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
        <Mail className="w-4 h-4 text-emerald-400" /> Wyślij mail zaproszenia (pojedynczy)
      </h3>
      <p className="text-zinc-500 text-xs mb-3">
        Wysyła maila z linkiem do claim'u konkretnej hodowli/firmie. Używaj dla indywidualnych przypadków (np. ktoś Cię poprosił).
        Normalnie scheduler robi to automatycznie.
      </p>
      <div className="flex gap-2 flex-wrap">
        <select
          value={collection}
          onChange={e => setCollection(e.target.value)}
          data-testid="send-single-collection-select"
          className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-emerald-500"
        >
          <option value="kennels">Hodowla</option>
          <option value="businesses">Firma</option>
        </select>
        <input
          type="text"
          placeholder="slug (np. wolve-vom-katzenberg)"
          value={slug}
          onChange={e => setSlug(e.target.value)}
          className="flex-1 min-w-[240px] px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-emerald-500"
          data-testid="send-single-slug-input"
        />
        <button
          onClick={send}
          disabled={loading || !slug.trim()}
          data-testid="send-single-btn"
          className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
          Wyślij
        </button>
      </div>
      {result && (
        <p className={`mt-2 text-xs ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
          {result.msg}
        </p>
      )}
    </div>
  );
}



export default function AdminPanel() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [trends, setTrends] = useState(null);
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, usersRes, subsRes, probRes, trendsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: headers() }),
        axios.get(`${API}/admin/users`, { headers: headers() }),
        axios.get(`${API}/admin/subscriptions`, { headers: headers() }),
        axios.get(`${API}/admin/problems`, { headers: headers() }),
        axios.get(`${API}/admin/trends`, { headers: headers() }),
      ]);
      setDashboard(dashRes.data);
      setTrends(trendsRes.data);
      setUsers(usersRes.data.users || []);
      setSubs(subsRes.data.subscriptions || []);
      setProblems(probRes.data.problems || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Brak dostępu — nie jesteś administratorem');
      } else {
        setError('Nie udało się załadować danych');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#060610] flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-white text-xl font-bold mb-2">{error}</p>
          <button onClick={() => navigate('/panel')} className="text-orange-400 hover:underline text-sm">
            Wróć do panelu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060610]">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/15 via-[#060610] to-[#060610]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,.15) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/panel')} className="text-gray-500 hover:text-white transition" data-testid="admin-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-xs text-gray-500">ApisAI Business Control</p>
            </div>
          </div>
          <button onClick={load} className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-white/10 hover:border-orange-500/30 rounded-lg transition" data-testid="admin-refresh">
            Odśwież
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="admin-tabs">
          <Tab label="Dashboard" active={tab === 'dashboard'} onClick={() => setTab('dashboard')} />
          <Tab label="Users" active={tab === 'users'} onClick={() => setTab('users')} count={users.length} />
          <Tab label="Subskrypcje" active={tab === 'subs'} onClick={() => setTab('subs')} count={subs.length} />
          <Tab label="Problemy" active={tab === 'problems'} onClick={() => setTab('problems')} count={problems.length} />
          <Tab label="Narzedzia" active={tab === 'tools'} onClick={() => setTab('tools')} />
          <Tab label="Moderacja" active={tab === 'moderation'} onClick={() => setTab('moderation')} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {tab === 'dashboard' && dashboard && <DashboardTab data={dashboard} trends={trends} />}
            {tab === 'users' && <UsersTab users={users} />}
            {tab === 'subs' && <SubsTab subs={subs} />}
            {tab === 'problems' && <ProblemsTab problems={problems} />}
            {tab === 'tools' && <SponsoredTogglesSection />}
            {tab === 'tools' && <DeleteUserByEmail />}
            {tab === 'tools' && <DeleteKennelSection />}
            {tab === 'tools' && <DeleteLitterBySlug />}
            {tab === 'tools' && <SendSingleClaimMail />}
            {tab === 'tools' && <ErrorLogsSection />}
            {tab === 'tools' && <BroadcastStatusSection />}
            {tab === 'tools' && <DripStatusSection />}
            {tab === 'tools' && <MigrateGooglePlacesSection />}
            {tab === 'tools' && <BackfillVoivodeshipSection />}
            {tab === 'tools' && <CleanupDemoLittersSection />}
            {tab === 'tools' && <DedupeSection />}
            {tab === 'tools' && <OrphanedSubmissionsSection />}
            {tab === 'tools' && <BreedCanonicalizeSection />}
            {tab === 'tools' && <KennelAuditSection />}
            {tab === 'moderation' && <ModerationTab />}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function ModerationTab() {
  const [submissions, setSubmissions] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [subRes, bizRes] = await Promise.all([
        axios.get(`${API}/admin/breeder-submissions?status=all`, { headers: headers() }),
        axios.get(`${API}/admin/businesses`, { headers: headers() }).catch(() => ({ data: { businesses: [] } })),
      ]);
      setSubmissions(subRes.data.submissions || subRes.data || []);
      setBusinesses(bizRes.data.businesses || bizRes.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAction = async (type, id, action) => {
    try {
      if (type === 'breeder') {
        if (action === 'delete') {
          await axios.put(`${API}/admin/breeder-submissions/${id}/review`, { action: 'reject' }, { headers: headers() });
        } else {
          await axios.put(`${API}/admin/breeder-submissions/${id}/review`, { action: 'approve' }, { headers: headers() });
        }
      } else {
        await axios.delete(`${API}/admin/businesses/${id}`, { headers: headers() });
      }
      toast.success(action === 'delete' ? 'Wpis usunięty' : 'Wpis zatwierdzony');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd');
    }
  };

  const allItems = [
    ...submissions.map(s => ({ ...s, _type: 'breeder', _name: s.kennel_name, _city: s.city, _status: s.status, _date: s.created_at, _id: s.id })),
    ...businesses.map(b => ({ ...b, _type: 'business', _name: b.name, _city: b.city, _status: 'active', _date: b.created_at, _id: b.id })),
  ].sort((a, b) => (b._date || '').localeCompare(a._date || ''));

  const filtered = filter === 'all' ? allItems : allItems.filter(i => i._status === filter);

  return (
    <div className="space-y-4" data-testid="moderation-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Moderacja wpisów</h2>
        <div className="flex gap-1">
          {['all', 'pending', 'active', 'approved', 'rejected', 'deleted'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${filter === f ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
              {f === 'all' ? 'Wszystkie' : f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-zinc-500 py-10">Brak wpisów</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => (
            <div key={`${item._type}-${item._id}`} className="bg-[#12121A] rounded-xl border border-zinc-800 p-4 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${item._type === 'breeder' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {item._type === 'breeder' ? 'Hodowla' : 'Firma'}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                    item._status === 'active' || item._status === 'approved' ? 'bg-green-500/20 text-green-400' :
                    item._status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    item._status === 'rejected' || item._status === 'deleted' ? 'bg-red-500/20 text-red-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>
                    {item._status}
                  </span>
                </div>
                <p className="text-white font-semibold text-sm">{item._name || '(brak nazwy)'}</p>
                <p className="text-zinc-500 text-xs">{item._city || '?'} {item.breeds ? `• ${(item.breeds || []).join(', ')}` : item.category ? `• ${item.category}` : ''}</p>
                <p className="text-zinc-600 text-xs mt-1">{item.phone || ''} {item.email || ''}</p>
                {item.description && <p className="text-zinc-500 text-xs mt-1 line-clamp-1">{item.description}</p>}
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                {item._status === 'pending' && item._type === 'breeder' && (
                  <button onClick={() => handleAction('breeder', item._id, 'approve')}
                    className="px-2.5 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium">
                    Zatwierdź
                  </button>
                )}
                {item._status !== 'deleted' && item._status !== 'rejected' && (
                  <button onClick={() => { if (window.confirm(`Usunąć "${item._name}"?`)) handleAction(item._type, item._id, 'delete'); }}
                    className="px-2.5 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-600 text-white text-xs font-medium">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DashboardTab({ data, trends }) {
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Userzy" value={data.users.total} sub={`+${data.users.new_month} ten miesiąc`} color="blue" />
        <StatCard icon={UserCheck} label="Onboarding" value={data.users.completed_onboarding} color="emerald" />
        <StatCard icon={FileText} label="Publikacje" value={data.publications.total} sub={`+${data.publications.this_week} ten tydzień`} color="purple" />
        <StatCard icon={Mail} label="Leady" value={data.leads.total} sub={`+${data.leads.this_week} ten tydzień`} color="orange" />
        <StatCard icon={TrendingUp} label="Klienci" value={data.leads.clients} color="emerald" />
        <StatCard icon={DollarSign} label="Przychód" value={`${data.revenue.total} zł`} color="amber" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={CreditCard} label="Płatne sub." value={data.subscriptions.active_paid} color="emerald" />
        <StatCard icon={Clock} label="Trial" value={data.subscriptions.trial} color="blue" />
        <StatCard icon={Eye} label="Odsłony" value={data.views.total} sub={`+${data.views.this_week} ten tydzień`} color="purple" />
        <StatCard icon={Users} label="Nowi (tydzień)" value={data.users.new_week} color="orange" />
      </div>

      {/* Trend Charts */}
      {trends && (
        <>
          <h3 className="text-sm font-semibold text-gray-400 mt-2">Trend 30 dni</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <TrendCard
              title="Nowi userzy"
              data={trends.users}
              color="#3b82f6"
              colorClass="from-blue-500/10 to-blue-600/5 border-blue-500/15"
              total={data.users.total}
              subLabel="razem"
            />
            <TrendCard
              title="Nowe leady"
              data={trends.leads}
              color="#f97316"
              colorClass="from-orange-500/10 to-orange-600/5 border-orange-500/15"
              total={data.leads.total}
              subLabel="razem"
            />
            <TrendCard
              title="Nowi klienci"
              data={trends.clients}
              color="#10b981"
              colorClass="from-emerald-500/10 to-emerald-600/5 border-emerald-500/15"
              total={data.leads.clients}
              subLabel="razem"
            />
            <TrendCard
              title="Publikacje"
              data={trends.publications}
              color="#a855f7"
              colorClass="from-purple-500/10 to-purple-600/5 border-purple-500/15"
              total={data.publications.total}
              subLabel="razem"
            />
            <TrendCard
              title="Odsłony stron"
              data={trends.views}
              color="#8b5cf6"
              colorClass="from-violet-500/10 to-violet-600/5 border-violet-500/15"
              total={data.views.total}
              subLabel="razem"
            />
          </div>
        </>
      )}
    </div>
  );
}

function UsersTab({ users }) {
  const [search, setSearch] = useState('');
  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  const planBadge = (plan, status) => {
    if (plan === 'pro') return <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-400 font-medium">PRO</span>;
    if (plan === 'trial') return <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-500/20 text-blue-400 font-medium">TRIAL</span>;
    return <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-500/20 text-gray-400 font-medium">FREE</span>;
  };

  return (
    <div data-testid="admin-users">
      <input
        type="text"
        placeholder="Szukaj po email lub nazwie..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full sm:w-80 mb-4 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-500 focus:border-orange-500/40 focus:outline-none transition"
        data-testid="admin-users-search"
      />
      <div className="bg-[#0c0c1a]/80 border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Email</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Typ</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Plan</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Leady</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Klienci</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Przychód</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Publikacje</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition">
                  <td className="px-4 py-3">
                    <p className="text-white text-sm">{u.email}</p>
                    <p className="text-gray-600 text-xs">{u.name || '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{u.profile_type || '—'}</td>
                  <td className="px-4 py-3">{planBadge(u.plan, u.sub_status)}</td>
                  <td className="px-4 py-3 text-right text-gray-300">{u.leads}</td>
                  <td className="px-4 py-3 text-right text-emerald-400">{u.clients}</td>
                  <td className="px-4 py-3 text-right text-amber-400">{u.revenue > 0 ? `${u.revenue} zł` : '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-300">{u.publications}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="text-center py-8 text-gray-500 text-sm">Brak wyników</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SubsTab({ subs }) {
  const planBadge = (plan) => {
    if (plan === 'pro') return <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-400 font-medium">PRO</span>;
    if (plan === 'trial') return <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-500/20 text-blue-400 font-medium">TRIAL</span>;
    return <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-500/20 text-gray-400 font-medium">{plan}</span>;
  };

  return (
    <div data-testid="admin-subs">
      {subs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Brak subskrypcji</p>
        </div>
      ) : (
        <div className="bg-[#0c0c1a]/80 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Email</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Plan</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Status</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Trial</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm">{s.email}</p>
                      <p className="text-gray-600 text-xs">{s.name || '—'}</p>
                    </td>
                    <td className="px-4 py-3">{planBadge(s.plan)}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{s.status}</td>
                    <td className="px-4 py-3 text-xs">
                      {s.plan === 'trial' ? (
                        <span className={s.trial_days_remaining > 0 ? 'text-blue-400' : 'text-red-400'}>
                          {s.trial_days_remaining > 0 ? `${s.trial_days_remaining} dni` : 'wygasł'} · {s.trial_publications_used}/{s.trial_publications_limit} pub.
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString('pl') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProblemsTab({ problems }) {
  const issueBadge = (issue) => {
    const colors = {
      'Brak profilu': 'bg-red-500/20 text-red-400',
      'Onboarding niezakończony': 'bg-amber-500/20 text-amber-400',
      'Brak zdjęć': 'bg-purple-500/20 text-purple-400',
      'Brak publikacji': 'bg-blue-500/20 text-blue-400',
      'Strona nieopublikowana': 'bg-orange-500/20 text-orange-400',
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mr-1 mb-1 ${colors[issue] || 'bg-gray-500/20 text-gray-400'}`}>
        {issue}
      </span>
    );
  };

  return (
    <div data-testid="admin-problems">
      {problems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30 text-emerald-400" />
          <p className="text-emerald-400">Brak problemów</p>
        </div>
      ) : (
        <div className="space-y-2">
          {problems.map(p => (
            <div key={p.user_id} className="bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-4 hover:border-orange-500/20 transition">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{p.email}</p>
                  <p className="text-gray-600 text-xs mb-2">{p.name || '—'}</p>
                  <div className="flex flex-wrap">
                    {p.issues.map((issue, i) => <span key={i}>{issueBadge(issue)}</span>)}
                  </div>
                </div>
                <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded-lg">
                  <AlertTriangle className="w-3 h-3" />
                  {p.issue_count}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function BackfillVoivodeshipSection() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [jobStatus, setJobStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  const run = async (apply) => {
    setLoading(true);
    setError('');
    setResult(null);
    setJobStatus(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/backfill/voivodeships?apply=${apply}`,
        {},
        { headers: headers(), timeout: 30000 }
      );
      setResult(data);
      if (apply && data.started) {
        setPolling(true);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Poll status while job is running
  useEffect(() => {
    if (!polling) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/backfill/voivodeships/status`, { headers: headers() });
        if (cancelled) return;
        setJobStatus(data);
        if (data.status === 'done' || data.status === 'error') {
          setPolling(false);
        }
      } catch {
        /* keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [polling]);

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="backfill-voivodeship-section">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white text-base font-semibold mb-1">Backfill: brakujące województwa</h3>
          <p className="text-gray-500 text-xs">
            Uzupełnia pole <code className="text-emerald-400">voivodeship</code> dla hodowli i firm na podstawie
            kodu pocztowego (95% skuteczności) lub nazwy miasta.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={() => run(false)}
          disabled={loading || polling}
          data-testid="backfill-dry-run-btn"
          className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-50 transition"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          1. Sprawdź (dry-run)
        </button>
        <button
          onClick={() => {
            if (window.confirm('Zapisać zmiany do bazy? Operacja jest bezpieczna (tylko dodaje brakujące województwa). Uruchomi się w tle — możesz zamknąć zakładkę.')) {
              run(true);
            }
          }}
          disabled={loading || !result || polling}
          data-testid="backfill-apply-btn"
          className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/20 disabled:opacity-30 transition"
        >
          2. Uruchom realnie
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400" data-testid="backfill-error">
          Błąd: {error}
        </div>
      )}

      {result && result.applied === false && (
        <div className="mt-4 space-y-3" data-testid="backfill-result">
          <div className="px-3 py-2 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/30">
            Tryb dry-run (nic nie zapisano) — szacunki na próbce 1000 dokumentów
          </div>
          {['kennels', 'businesses'].map((col) => (
            <div key={col} className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
              <p className="text-white text-sm font-medium mb-1">{col === 'kennels' ? 'Hodowle' : 'Firmy'}</p>
              <p className="text-xs text-gray-500">
                Kandydaci: <span className="text-white">{result[col].candidates}</span>
                {' • '}
                Do aktualizacji{result[col].sampled ? ' (szacunek)' : ''}: <span className="text-emerald-400">{result[col].updated}</span>
                {' • '}
                Bez dopasowania: <span className="text-amber-400">{result[col].skipped}</span>
              </p>
              <p className="text-[11px] text-gray-600 mt-1">
                Kod pocztowy: <span className="text-white">{result[col].by_strategy.postal_code}</span>
                {' • '}
                Miasto: <span className="text-white">{result[col].by_strategy.city_name}</span>
                {' • '}
                Brak: <span className="text-white">{result[col].by_strategy.none}</span>
              </p>
            </div>
          ))}
        </div>
      )}

      {result && result.started && (
        <div className="mt-4 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
          ✓ Uruchomiono w tle — sprawdzam postęp...
        </div>
      )}

      {jobStatus && (
        <div className="mt-4 space-y-3" data-testid="backfill-job-status">
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${
            jobStatus.status === 'done' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' :
            jobStatus.status === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
            'bg-blue-500/10 text-blue-400 border border-blue-500/30'
          }`}>
            Status: {jobStatus.status === 'done' ? '✓ Zakończone' : jobStatus.status === 'error' ? '✗ Błąd' : '⟳ W toku...'}
          </div>
          {jobStatus.status === 'error' && (
            <p className="text-xs text-red-400">{jobStatus.error}</p>
          )}
          {jobStatus.status === 'done' && jobStatus.kennels && (
            <div className="space-y-2">
              {['kennels', 'businesses'].map((col) => (
                <div key={col} className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
                  <p className="text-white text-sm font-medium mb-1">{col === 'kennels' ? 'Hodowle' : 'Firmy'}</p>
                  <p className="text-xs text-gray-500">
                    Zapisano: <span className="text-emerald-400">{jobStatus[col].updated}</span>
                    {' z '}
                    <span className="text-white">{jobStatus[col].candidates}</span>
                    {' • Pominięto: '}
                    <span className="text-amber-400">{jobStatus[col].skipped}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CleanupDemoLittersSection() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async (apply) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/admin/cleanup/demo-litters?apply=${apply}`,
        {},
        { headers: headers(), timeout: 30000 }
      );
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="cleanup-demo-litters-section">
      <h3 className="text-white text-base font-semibold mb-1">🗑 Usuń demo/testowe mioty</h3>
      <p className="text-gray-500 text-xs mb-4">
        Kasuje fikcyjne mioty z bazy. Matchuje: <code className="text-emerald-400">DEMO</code>, <code className="text-emerald-400">Miot A/B/C</code>, <code className="text-emerald-400">Miot Wiosenny/Letni/Jesienny/Zimowy</code>, <code className="text-emerald-400">Miot Planowany/Testowy</code>, albo kennel_slug <code className="text-emerald-400">apisai, demo, test</code>.
        Najpierw „Sprawdź (dry-run)", potem „Usuń realnie".
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => run(false)}
          disabled={loading}
          data-testid="cleanup-demo-dry-btn"
          className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-50 transition"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          1. Sprawdź (dry-run)
        </button>
        <button
          onClick={() => {
            if (window.confirm('Usunąć TRWALE demo mioty z bazy? Operacja nieodwracalna.')) run(true);
          }}
          disabled={loading || !result || result.would_delete === 0}
          data-testid="cleanup-demo-apply-btn"
          className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 disabled:opacity-30 transition"
        >
          2. Usuń realnie
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
          Błąd: {error}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-2" data-testid="cleanup-demo-result">
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${result.applied ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/10 text-blue-400 border border-blue-500/30'}`}>
            {result.applied ? `✓ Usunięto ${result.deleted} miotów` : `Znalazłem ${result.would_delete} miotów do usunięcia (dry-run)`}
          </div>
          {(result.samples || []).length > 0 && (
            <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-3 space-y-1">
              <p className="text-[11px] text-gray-500 font-medium mb-1">Przykłady:</p>
              {result.samples.slice(0, 10).map((s, i) => (
                <p key={i} className="text-xs text-gray-400">
                  <span className="text-white">{s.name}</span> — {s.breed} (kennel: <span className="text-amber-400">{s.kennel_slug}</span>)
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function DedupeSection() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [collection, setCollection] = useState('kennels');

  const run = async (apply) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/admin/cleanup/duplicates?collection=${collection}&apply=${apply}`,
        {},
        { headers: headers(), timeout: 90000 }
      );
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="dedupe-section">
      <h3 className="text-white text-base font-semibold mb-1">🔀 Deduplikacja (name + miasto)</h3>
      <p className="text-gray-500 text-xs mb-4">
        Łączy duplikaty tej samej hodowli/firmy (np. „Wölve Vom Katzenberg" 3× w Trzebnicy). 
        Zachowuje 1 rekord (priorytet: claimowany &gt; z wysłanym mailem &gt; najnowszy), resztę robi soft-delete.
      </p>

      <div className="flex gap-2 items-center mb-3">
        <label className="text-xs text-gray-400">Kolekcja:</label>
        <select
          value={collection}
          onChange={e => { setCollection(e.target.value); setResult(null); }}
          data-testid="dedupe-collection-select"
          className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-white text-xs rounded"
        >
          <option value="kennels">Hodowle</option>
          <option value="businesses">Firmy</option>
        </select>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => run(false)}
          disabled={loading}
          data-testid="dedupe-dry-btn"
          className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-50 transition"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          1. Sprawdź (dry-run)
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Zdeduplikować ${collection === 'kennels' ? 'hodowle' : 'firmy'}? Duplikaty zostaną soft-deleted (można odzyskać).`)) run(true);
          }}
          disabled={loading || !result || result.would_delete === 0}
          data-testid="dedupe-apply-btn"
          className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 disabled:opacity-30 transition"
        >
          2. Usuń duplikaty
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
          Błąd: {error}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-2" data-testid="dedupe-result">
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${result.applied ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/10 text-blue-400 border border-blue-500/30'}`}>
            {result.applied
              ? `✓ Usunięto ${result.deleted} duplikatów (z ${result.groups_deduplicated} grup)`
              : `Znalazłem ${result.would_delete} duplikatów do usunięcia z ${result.groups_with_duplicates} grup (dry-run)`}
          </div>
          {(result.samples || []).length > 0 && (
            <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-3 space-y-1 max-h-80 overflow-y-auto">
              <p className="text-[11px] text-gray-500 font-medium mb-1">Przykłady:</p>
              {result.samples.map((s, i) => (
                <p key={i} className="text-xs text-gray-400">
                  <span className="text-white">{s.name}</span> @ <span className="text-gray-300">{s.city || '—'}</span> ({s.count}×)
                  {' '}→ zachowam <span className="text-emerald-400">{s.keep_slug}</span>, usunę{' '}
                  <span className="text-red-400">{s.delete_slugs.join(', ')}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function BreedCanonicalizeSection() {
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scan, setScan] = useState(null);
  const [error, setError] = useState('');

  const runScan = async () => {
    setScanning(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/breeds/scan-variants`, { headers: headers() });
      setScan(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!window.confirm(`Zastosować kanonizację? Zmieni ${scan?.changes?.reduce((a,g) => a + g.rename_count, 0) || 0} wpisów w bazie. To trwała zmiana — backup robić ręcznie!`)) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/breeds/canonicalize?dry_run=false`, {}, { headers: headers() });
      alert(`✓ Zastosowane!\n${JSON.stringify(res.data.stats, null, 2)}`);
      await runScan();
    } catch (e) {
      alert(`Błąd: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="breed-canonicalize-section">
      <h3 className="text-white text-base font-semibold mb-1">🐕 Scal warianty ras (kanonizacja)</h3>
      <p className="text-gray-500 text-xs mb-4">
        Mapuje warianty rasy (np. „Owczarek Niemiecki Długowłosy", „Husky Syberyjski", „JRT")
        na jedną kanoniczną nazwę („Owczarek Niemiecki", „Siberian Husky", „Jack Russell Terrier").
        Po kanonizacji w katalogu i filtrach pojawia się jedna pozycja zamiast kilku.
      </p>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={runScan}
          disabled={scanning}
          data-testid="scan-breeds-btn"
          className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-50 transition"
        >
          {scanning ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          Skanuj bazę (preview)
        </button>
        {scan && scan.groups_with_changes > 0 && (
          <button
            onClick={apply}
            disabled={applying}
            data-testid="apply-breeds-canonicalize-btn"
            className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/20 disabled:opacity-50 transition"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
            Zastosuj zmiany w DB
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
          Błąd: {error}
        </div>
      )}

      {scan && (
        <div className="mt-4 space-y-2">
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${scan.groups_with_changes === 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'}`}>
            {scan.groups_with_changes === 0
              ? `✓ Wszystkie ${scan.total_distinct_breeds} ras są już skanonizowane`
              : `⚠️ ${scan.groups_with_changes} ras ma warianty do scalenia (z ${scan.total_distinct_breeds} wszystkich)`}
          </div>
          {(scan.changes || []).map((g) => (
            <div key={g.canonical} className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
              <p className="text-emerald-400 text-sm font-medium">→ {g.canonical}</p>
              <p className="text-gray-500 text-[11px] mt-0.5 mb-2">{g.rename_count} wpisów do zmiany</p>
              <div className="space-y-1">
                {g.variants.map((v) => (
                  <div key={v.raw} className="text-[11px] text-gray-400">
                    <span className="font-mono text-gray-300">{v.count}×</span> {v.raw}
                    {v.raw === g.canonical && <span className="ml-2 text-emerald-500/70">(kanoniczna — zostaje)</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function OrphanedSubmissionsSection() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [repairing, setRepairing] = useState({});

  const runDiagnose = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/submissions/diagnose`, { headers: headers() });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const repairOne = async (submission_id, kennel_name) => {
    if (!window.confirm(`Naprawić zgłoszenie „${kennel_name}"? System spróbuje przywrócić/utworzyć hodowlę w katalogu.`)) return;
    setRepairing(prev => ({ ...prev, [submission_id]: true }));
    try {
      const res = await axios.post(`${API}/admin/submissions/${submission_id}/republish`, {}, { headers: headers() });
      alert(`✓ ${res.data.action}\nSlug: ${res.data.kennel_slug}\nID: ${res.data.kennel_id}`);
      await runDiagnose(); // refresh
    } catch (e) {
      alert(`Błąd: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRepairing(prev => ({ ...prev, [submission_id]: false }));
    }
  };

  const rejectAsDuplicate = async (submission_id, kennel_name) => {
    if (!window.confirm(`Oznaczyć „${kennel_name}" jako duplikat? Zgłoszenie zniknie z listy (ale zostanie w bazie jako rejected_duplicate).`)) return;
    setRepairing(prev => ({ ...prev, [submission_id]: true }));
    try {
      await axios.post(`${API}/admin/submissions/${submission_id}/reject-as-duplicate`, {}, { headers: headers() });
      await runDiagnose();
    } catch (e) {
      alert(`Błąd: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRepairing(prev => ({ ...prev, [submission_id]: false }));
    }
  };

  const ISSUE_LABELS = {
    never_created_kennel: '❌ Hodowla nigdy nie została utworzona',
    missing_kennel_id_but_kennel_exists: '⚠️ Brak linku do hodowli (ale istnieje w bazie)',
    kennel_deleted_hard: '💀 Hodowla usunięta twardym usunięciem',
    kennel_soft_deleted: '🗑️ Hodowla miękko usunięta (deleted_at)',
    kennel_missing_required_fields: '⚠️ Brakuje wymaganych pól (name/slug/breeds)',
  };

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="orphaned-submissions-section">
      <h3 className="text-white text-base font-semibold mb-1">🔧 Napraw „zatwierdzone ale niewidoczne" zgłoszenia</h3>
      <p className="text-gray-500 text-xs mb-4">
        Znajduje zgłoszenia ze statusem <code>active</code> których hodowla nie jest widoczna w publicznym katalogu
        (np. hodowla została usunięta, approve failował w połowie, lub brak pól wymaganych). Klikasz „Napraw" — system
        przywraca lub tworzy hodowlę na podstawie danych zgłoszenia.
      </p>

      <button
        onClick={runDiagnose}
        disabled={loading}
        data-testid="diagnose-orphans-btn"
        className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-50 transition"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
        Znajdź problemy
      </button>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
          Błąd: {error}
        </div>
      )}

      {data && (
        <div className="mt-4 space-y-2">
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${data.problems_count === 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'}`}>
            {data.problems_count === 0
              ? `✓ Wszystkie ${data.total_active_submissions} zatwierdzone zgłoszenia są widoczne w katalogu`
              : `⚠️ ${data.problems_count} z ${data.total_active_submissions} zatwierdzonych zgłoszeń NIE jest w katalogu`}
          </div>
          {(data.problems || []).map((p) => (
            <div key={p.submission_id} className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{p.kennel_name}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{p.city} • {(p.breeds || []).join(', ')}</p>
                  <p className="text-gray-500 text-[11px] mt-1">
                    {p.phone} {p.email && `• ${p.email}`}
                  </p>
                  <p className="text-amber-400 text-xs mt-1.5">
                    {ISSUE_LABELS[p.issue] || p.issue}
                  </p>
                  {p.kennel_snapshot && (
                    <p className="text-gray-600 text-[11px] mt-1">
                      kennel: {p.kennel_snapshot.slug || '—'} · status: {p.kennel_snapshot.public_status || '—'} · deleted_at: {p.kennel_snapshot.deleted_at || '—'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => repairOne(p.submission_id, p.kennel_name)}
                  disabled={!!repairing[p.submission_id]}
                  data-testid={`repair-submission-${p.submission_id}`}
                  className="flex-shrink-0 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/20 disabled:opacity-50 transition"
                >
                  {repairing[p.submission_id] ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  Napraw
                </button>
                <button
                  onClick={() => rejectAsDuplicate(p.submission_id, p.kennel_name)}
                  disabled={!!repairing[p.submission_id]}
                  data-testid={`reject-duplicate-${p.submission_id}`}
                  className="flex-shrink-0 px-3 py-1.5 bg-gray-500/10 border border-gray-500/30 text-gray-400 rounded-lg text-xs font-medium hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 disabled:opacity-50 transition"
                  title="Oznacz jako duplikat — ukryje z listy"
                >
                  Duplikat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function KennelAuditSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');

  const run = async () => {
    setLoading(true); setErr(''); setSelected(new Set());
    try {
      const res = await axios.get(`${API}/admin/audit/kennels`);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const selectAllCategory = (catItems) => {
    const s = new Set(selected);
    catItems.forEach(it => s.add(it.id));
    setSelected(s);
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Usunąć ${selected.size} hodowli (soft-delete)?`)) return;
    setDeleting(true); setErr('');
    try {
      const res = await axios.post(`${API}/admin/audit/kennels/bulk-delete`, {
        ids: Array.from(selected),
        reason: 'admin_audit_ui',
      });
      alert(`Usunięto: ${res.data?.deleted || 0}`);
      setSelected(new Set());
      await run();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-6 p-5 bg-white/5 border border-white/10 rounded-2xl" data-testid="admin-audit-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-lg font-bold text-white">🔍 Audit bazy hodowli</h3>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={bulkDelete} disabled={deleting}
              className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold disabled:opacity-50"
              data-testid="audit-bulk-delete"
            >
              {deleting ? 'Usuwam...' : `Usuń zaznaczone (${selected.size})`}
            </button>
          )}
          <button
            onClick={run} disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-black text-xs font-semibold disabled:opacity-50"
            data-testid="audit-run-btn"
          >
            {loading ? 'Skanuję...' : 'Uruchom audit'}
          </button>
        </div>
      </div>

      {err && <div className="mb-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">{err}</div>}

      {!data && !loading && (
        <p className="text-sm text-gray-400">
          Skanuje wszystkie aktywne hodowle pod kątem: związków, klubów, stowarzyszeń, sklepów zoologicznych, schronisk,
          nazwy &lt;3 znaki. Kliknij „Uruchom audit" żeby sprawdzić.
        </p>
      )}

      {data && (
        <>
          <div className="flex items-center gap-4 mb-4 text-sm">
            <span className="text-gray-400">Przeskanowano:</span>
            <span className="font-bold text-white">{data.total_scanned}</span>
            <span className="text-gray-400">Podejrzanych:</span>
            <span className={`font-bold ${data.total_suspicious > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
              {data.total_suspicious}
            </span>
          </div>

          {data.total_suspicious === 0 && (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm">
              ✅ Baza czysta — nie znaleziono podejrzanych wpisów.
            </div>
          )}

          {Object.entries(data.categories || {}).map(([cat, items]) => {
            if (items.length === 0) return null;
            const catLabel = {
              association: '🏛️ Związki / Kluby / Stowarzyszenia',
              non_kennel_keyword: '🏪 Sklepy / Schroniska / Salony',
              too_short: '⚠️ Zbyt krótka nazwa',
              empty_name: '❌ Pusta nazwa',
            }[cat] || cat;
            return (
              <div key={cat} className="mb-4" data-testid={`audit-cat-${cat}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-white">{catLabel} ({items.length})</h4>
                  <button
                    onClick={() => selectAllCategory(items)}
                    className="text-xs text-orange-300 hover:text-orange-200"
                    data-testid={`audit-select-cat-${cat}`}
                  >
                    Zaznacz wszystkie
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-2">{data.meta?.[cat]}</p>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {items.map(it => (
                    <label
                      key={it.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition ${
                        selected.has(it.id) ? 'bg-rose-500/10 border-rose-500/40' : 'bg-white/[0.02] border-white/5 hover:bg-white/5'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(it.id)}
                        onChange={() => toggle(it.id)}
                        className="w-4 h-4 accent-rose-500"
                        data-testid={`audit-check-${it.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{it.name}</div>
                        <div className="text-xs text-gray-500">
                          {it.city} · /hodowla/{it.slug} · {it.source || 'manual'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}




function BroadcastStatusSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/broadcast/status`, { headers: headers() });
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleScheduler = async (nextEnabled) => {
    if (toggling) return;
    // Bezpieczeństwo: potwierdzenie przy włączaniu (uniknij przypadkowego startu mass-maili)
    if (nextEnabled && !window.confirm('Na pewno WŁĄCZYĆ scheduler? Zacznie rozsyłać maile claim do Google Places (limity ramp-up: 100 → 250 → 500/dzień).')) {
      return;
    }
    setToggling(true);
    setError('');
    try {
      await axios.post(`${API}/admin/broadcast/toggle`, { enabled: nextEnabled }, { headers: headers() });
      await fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setToggling(false);
    }
  };

  useEffect(() => { fetchStatus(); const t = setInterval(fetchStatus, 15000); return () => clearInterval(t); }, []);

  const enabled = status?.enabled;
  const enabledViaEnv = status?.enabled_via_env;
  const sent = status?.sent_today ?? 0;
  const limit = status?.daily_limit ?? 0;
  const pct = limit > 0 ? Math.min(100, Math.round((sent / limit) * 100)) : 0;

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="broadcast-status-section">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white text-base font-semibold mb-1">📬 Status broadcastu maili</h3>
          <p className="text-gray-500 text-xs">Automatyczne odświeżanie co 15s. Pokazuje czy scheduler wysyła maile claim.</p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          data-testid="broadcast-refresh-btn"
          className="px-3 py-1.5 bg-white/5 border border-white/10 text-gray-300 rounded-lg text-xs hover:bg-white/10"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Odśwież'}
        </button>
      </div>

      {error && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">Błąd: {error}</div>}

      {status && (
        <>
          {/* Główny status + toggle ON/OFF */}
          <div className={`mt-3 px-4 py-3 rounded-xl border ${
            enabled
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${enabled ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="broadcast-enabled-state">
                  {enabled ? '✅ Scheduler AKTYWNY — wysyła maile' : '⏸ Scheduler WYŁĄCZONY'}
                </p>
                <p className="text-xs mt-1 text-gray-400">
                  {enabledViaEnv
                    ? 'Włączony przez zmienną środowiskową CLAIM_BROADCAST_ENABLED=true'
                    : (enabled ? 'Włączony z panelu admina (DB flag).' : 'Kliknij przycisk aby włączyć rozsyłkę.')}
                </p>
              </div>
              {!enabledViaEnv && (
                <button
                  onClick={() => toggleScheduler(!enabled)}
                  disabled={toggling}
                  data-testid="broadcast-toggle-btn"
                  className={`shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    enabled
                      ? 'bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30'
                      : 'bg-emerald-500 border border-emerald-400 text-white hover:bg-emerald-600'
                  } disabled:opacity-50`}
                >
                  {toggling ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (enabled ? 'Wyłącz' : 'Włącz scheduler')}
                </button>
              )}
            </div>
          </div>

          {/* Licznik dzisiejszych wysyłek */}
          {enabled && (
            <div className="mt-4 bg-[#050510]/50 border border-white/5 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-white font-medium">Dzisiejsza wysyłka</p>
                <p className="text-xs text-gray-500">Dzień {status.day_number ?? 1} z ramp-up'u</p>
              </div>
              <div className="flex items-baseline gap-3 mb-3">
                <p className="text-3xl font-bold text-emerald-400" data-testid="broadcast-sent-count">{sent}</p>
                <p className="text-sm text-gray-400">/ {limit} maili</p>
              </div>
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Ramp-up: dzień 1-2 = 100, dzień 3-5 = 250, dzień 6+ = 500/dzień
              </p>
            </div>
          )}

          {/* Statystyki pending / total */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Do wysłania</p>
              <p className="text-lg font-bold text-white" data-testid="broadcast-pending-total">{status.pending_total ?? 0}</p>
            </div>
            <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Wysłane łącznie</p>
              <p className="text-lg font-bold text-emerald-400">{status.total_sent_all_time ?? 0}</p>
            </div>
            <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Claimów</p>
              <p className="text-lg font-bold text-amber-400">{status.claims_received ?? 0}</p>
            </div>
          </div>

          {/* Ostatnia aktywność */}
          {status.last_run_at && (
            <p className="text-xs text-gray-500 mt-3">
              Ostatni tick: <span className="text-gray-300">{new Date(status.last_run_at).toLocaleString('pl-PL')}</span>
            </p>
          )}

          {/* Wykres 14-dniowy */}
          <BroadcastHistoryChart />
        </>
      )}
    </div>
  );
}

function BroadcastHistoryChart() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const fetchHistory = async () => {
      try {
        const { data: resp } = await axios.get(`${API}/admin/broadcast/history?days=14`, { headers: headers() });
        if (cancel) return;
        setData(resp.history.map(d => ({
          ...d,
          day: new Date(d.date).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }),
        })));
        setSummary(resp.summary);
      } catch (e) {
        if (!cancel) setData([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchHistory();
    const t = setInterval(fetchHistory, 60000); // odśwież co 60s
    return () => { cancel = true; clearInterval(t); };
  }, []);

  if (loading) {
    return <div className="mt-4 p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-gray-400" /></div>;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="mt-5 pt-5 border-t border-white/5" data-testid="broadcast-history-chart">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-white text-sm font-semibold">📊 Aktywność — ostatnie 14 dni</h4>
        {summary && (
          <div className="flex gap-4 text-xs">
            <span className="text-gray-500">Wysłane: <span className="text-emerald-400 font-bold">{summary.total_sent}</span></span>
            <span className="text-gray-500">Claimy: <span className="text-amber-400 font-bold">{summary.total_claims}</span></span>
            <span className="text-gray-500">Konwersja: <span className="text-blue-400 font-bold">{summary.conversion_rate_pct}%</span></span>
          </div>
        )}
      </div>

      <div className="bg-[#050510]/50 border border-white/5 rounded-xl p-3">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0c0c1a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                fontSize: 12,
                color: '#fff',
              }}
              labelStyle={{ color: '#94a3b8', marginBottom: 4 }}
              formatter={(value, name) => [value, name === 'sent' ? 'Wysłane maile' : 'Claimy']}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(val) => val === 'sent' ? 'Wysłane maile' : 'Claimy (leady)'}
            />
            <Line
              type="monotone"
              dataKey="sent"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="claims"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}



function MigrateGooglePlacesSection() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0, phase: '' });

  const handlePullFromPreview = async () => {
    if (pulling) return;
    const sourceUrl = window.prompt(
      'URL preview (tam gdzie są dane):',
      'https://scout-chat-flow.preview.emergentagent.com'
    );
    if (!sourceUrl) return;
    const password = window.prompt('Wpisz hasło admina (to samo co do logowania):');
    if (!password) return;

    setPulling(true);
    setError('');
    setResult(null);
    setProgress({ done: 0, total: 0, phase: 'Uruchamiam import w tle...' });

    try {
      // 1) Uruchom background job
      const { data: startResp } = await axios.post(
        `${API}/admin/import/pull-from-preview`,
        { source_url: sourceUrl, admin_password: password },
        { headers: headers(), timeout: 30000 }
      );
      const jobId = startResp.job_id;
      if (!jobId) throw new Error('Brak job_id w odpowiedzi');

      // 2) Polling statusu co 3s (max 10 min = 200 iteracji)
      let iterations = 0;
      const MAX_ITER = 200;
      while (iterations < MAX_ITER) {
        await new Promise(r => setTimeout(r, 3000));
        iterations += 1;
        const { data: status } = await axios.get(
          `${API}/admin/import/pull-status?job_id=${jobId}`,
          { headers: headers(), timeout: 15000 }
        );

        const prog = status.progress || {};
        setProgress({
          done: prog.done ?? 0,
          total: prog.total ?? 0,
          phase: prog.phase || 'Pracuję...',
        });

        if (status.status === 'done') {
          setResult({
            total_upserted: (status.result || {}).upserted || 0,
            total_skipped: (status.result || {}).skipped || 0,
          });
          break;
        }
        if (status.status === 'error') {
          throw new Error(status.error || 'Import nie powiódł się');
        }
      }
      if (iterations >= MAX_ITER) {
        throw new Error('Timeout — import trwa zbyt długo (>10 min)');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Pull nie powiódł się');
    } finally {
      setPulling(false);
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setError('');
    try {
      const resp = await axios.get(`${API}/admin/export/google-places`, {
        headers: headers(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `places_export_${today}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Eksport nie powiódł się');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || importing) return;
    if (!window.confirm(`Importować ${selectedFile.name}? Wyślę plik w paczkach po 300 rekordów.`)) return;

    setImporting(true);
    setError('');
    setResult(null);
    setProgress({ done: 0, total: 0, phase: 'Parsowanie pliku...' });

    try {
      // 1) Wczytaj plik JSON client-side
      const text = await selectedFile.text();
      const data = JSON.parse(text);
      const businesses = Array.isArray(data.businesses) ? data.businesses : [];
      const kennels = Array.isArray(data.kennels) ? data.kennels : [];

      const CHUNK = 100; // ~150KB per request → bezpiecznie pod limitem ingress prod
      const totalItems = businesses.length + kennels.length;
      setProgress({ done: 0, total: totalItems, phase: 'Wysyłam...' });

      const totals = { upserted: 0, skipped: 0 };
      let done = 0;

      const sendChunks = async (items, collection) => {
        for (let i = 0; i < items.length; i += CHUNK) {
          const chunk = items.slice(i, i + CHUNK);
          const { data: resp } = await axios.post(
            `${API}/admin/import/google-places-chunk`,
            { collection, items: chunk },
            { headers: headers(), timeout: 60000 }
          );
          totals.upserted += resp.upserted || 0;
          totals.skipped += resp.skipped || 0;
          done += chunk.length;
          setProgress({ done, total: totalItems, phase: `Wysyłam ${collection}...` });
        }
      };

      await sendChunks(businesses, 'businesses');
      await sendChunks(kennels, 'kennels');

      setResult({
        total_upserted: totals.upserted,
        total_skipped: totals.skipped,
        result: {
          businesses: `${businesses.length} wysłanych`,
          kennels: `${kennels.length} wysłanych`,
        },
      });
      setSelectedFile(null);
      setProgress({ done: totalItems, total: totalItems, phase: 'Gotowe' });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Import nie powiódł się');
    } finally {
      setImporting(false);
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="migrate-places-section">
      <h3 className="text-white text-base font-semibold mb-1">🔄 Migracja Google Places (preview ↔ prod)</h3>
      <p className="text-gray-500 text-xs mb-4">
        Przenieś firmy i hodowle ze scrapera między środowiskami. <strong>Najszybsza metoda</strong>: przycisk „Pobierz z preview" poniżej.
      </p>

      {/* 🚀 PULL Z PREVIEW — ZALECANA METODA (jeden klik) */}
      <div className="bg-gradient-to-br from-purple-500/10 to-emerald-500/10 border border-purple-500/30 rounded-lg p-4 mb-3">
        <p className="text-sm text-white font-semibold mb-1">🚀 Metoda zalecana: Pobierz bezpośrednio z preview</p>
        <p className="text-xs text-gray-400 mb-3">
          Prod sam połączy się z preview i pobierze wszystkie firmy/hodowle. Jeden klik, jeden request, zero uploadów plików.
        </p>
        <button
          onClick={handlePullFromPreview}
          disabled={pulling}
          data-testid="migrate-pull-btn"
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
        >
          {pulling ? <><Loader2 className="w-4 h-4 animate-spin" /> Pobieram z preview...</> : '🚀 Pobierz z preview (jeden klik)'}
        </button>

        {/* Pasek postępu dla pull */}
        {pulling && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{progress.phase || 'Pracuję...'}</span>
              {progress.total > 0 && <span>{progress.done} / {progress.total}</span>}
            </div>
            <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all"
                style={{ width: progress.total > 0 ? `${Math.round((progress.done / progress.total) * 100)}%` : '5%' }}
              />
            </div>
          </div>
        )}
      </div>

      <details className="mb-3">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300">Alternatywa: ręczny eksport/import plikiem JSON</summary>
        <div className="mt-3">

          {/* EXPORT */}
          <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-4 mb-3">
            <p className="text-sm text-white font-medium mb-1">1. Eksport (uruchom na preview)</p>
            <p className="text-xs text-gray-500 mb-3">
              Pobierze plik JSON ze wszystkimi firmami i hodowlami z Google Places.
            </p>
            <button
              onClick={handleExport}
              disabled={exporting}
              data-testid="migrate-export-btn"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            >
              {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /> Eksportuję...</> : 'Pobierz plik (export)'}
            </button>
          </div>

          {/* IMPORT */}
          <div className="bg-[#050510]/50 border border-white/5 rounded-lg p-4">
            <p className="text-sm text-white font-medium mb-1">2. Import (uruchom na prod)</p>
            <p className="text-xs text-gray-500 mb-3">
              Wybierz pobrany plik JSON i kliknij „Importuj". Plik zostanie podzielony na małe paczki
              po 300 rekordów (omija limit uploadów na prod). Istniejące claimowane firmy są chronione.
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <label className="px-3 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-lg text-xs hover:bg-white/10 cursor-pointer">
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => { setSelectedFile(e.target.files?.[0] || null); setResult(null); setError(''); setProgress({ done: 0, total: 0, phase: '' }); }}
                  className="hidden"
                  data-testid="migrate-file-input"
                />
                {selectedFile ? `📄 ${selectedFile.name}` : 'Wybierz plik JSON'}
              </label>
              <button
                onClick={handleImport}
                disabled={!selectedFile || importing}
                data-testid="migrate-import-btn"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-2"
              >
                {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importuję...</> : 'Importuj plik'}
              </button>
            </div>
            {selectedFile && !importing && !result && (
              <p className="text-xs text-gray-400 mt-2">Rozmiar pliku: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            )}

            {/* Pasek postępu */}
            {importing && progress.total > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{progress.phase}</span>
                  <span>{progress.done} / {progress.total} ({pct}%)</span>
                </div>
                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </details>

      {error && <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400" data-testid="migrate-error">Błąd: {error}</div>}

      {result && (
        <div className="mt-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg" data-testid="migrate-result">
          <p className="text-sm font-semibold text-emerald-400 mb-2">✅ Import zakończony</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-gray-500">Dodane / zaktualizowane</p>
              <p className="text-2xl font-bold text-emerald-400">{result.total_upserted ?? 0}</p>
            </div>
            <div>
              <p className="text-gray-500">Pominięte (claimowane)</p>
              <p className="text-2xl font-bold text-gray-400">{result.total_skipped ?? 0}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Odśwież sekcję „Status broadcastu" — „DO WYSŁANIA" pokaże nowe firmy.</p>
        </div>
      )}
    </div>
  );
}

function DripStatusSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/drip/status`, { headers: headers() });
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleDrip = async (nextEnabled) => {
    if (toggling) return;
    if (nextEnabled && !window.confirm('Włączyć drip campaign? Scheduler zacznie wysyłać follow-up maile (D+1, D+3, D+14, D+27, D+30) oraz robić auto-downgrade po 30 dniach trialu.')) {
      return;
    }
    setToggling(true);
    setError('');
    try {
      await axios.post(`${API}/admin/drip/toggle`, { enabled: nextEnabled }, { headers: headers() });
      await fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setToggling(false);
    }
  };

  useEffect(() => { fetchStatus(); const t = setInterval(fetchStatus, 30000); return () => clearInterval(t); }, []);

  const stages = [
    { key: 'd1',  label: 'D+1',  desc: 'Dodaj zdjęcia',  color: 'text-emerald-400' },
    { key: 'd3',  label: 'D+3',  desc: 'Dodaj mioty',    color: 'text-emerald-400' },
    { key: 'd14', label: 'D+14', desc: 'Połowa trialu',  color: 'text-blue-400' },
    { key: 'd27', label: 'D+27', desc: '3 dni do końca', color: 'text-amber-400' },
    { key: 'd30', label: 'D+30', desc: 'Auto-downgrade', color: 'text-red-400' },
  ];

  const enabled = status?.enabled;

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="drip-status-section">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white text-base font-semibold mb-1">💧 Drip campaign (follow-up po claim)</h3>
          <p className="text-gray-500 text-xs">Automatyczne przypomnienia D+1, D+3, D+14, D+27, D+30 + auto-downgrade do Basic po trial'u.</p>
        </div>
        <button onClick={fetchStatus} disabled={loading} data-testid="drip-refresh-btn" className="px-3 py-1.5 bg-white/5 border border-white/10 text-gray-300 rounded-lg text-xs hover:bg-white/10">
          {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Odśwież'}
        </button>
      </div>

      {error && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">Błąd: {error}</div>}

      {status && (
        <>
          {/* Toggle */}
          <div className={`mt-3 px-4 py-3 rounded-xl border ${enabled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${enabled ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="drip-enabled-state">
                  {enabled ? '✅ Drip AKTYWNY' : '⏸ Drip WYŁĄCZONY'}
                </p>
                <p className="text-xs mt-1 text-gray-400">
                  {enabled
                    ? 'Cykl co 60 min — sprawdza wszystkich claimowanych i wysyła właściwe maile.'
                    : 'Kliknij aby uruchomić follow-up kampanię (odzyskuje 30-50% porzuconych trialów).'}
                </p>
              </div>
              <button
                onClick={() => toggleDrip(!enabled)}
                disabled={toggling}
                data-testid="drip-toggle-btn"
                className={`shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  enabled
                    ? 'bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30'
                    : 'bg-emerald-500 border border-emerald-400 text-white hover:bg-emerald-600'
                } disabled:opacity-50`}
              >
                {toggling ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (enabled ? 'Wyłącz' : 'Włącz drip')}
              </button>
            </div>
          </div>

          {/* Stage breakdown */}
          <div className="mt-4 grid grid-cols-5 gap-2">
            {stages.map(s => (
              <div key={s.key} className="bg-[#050510]/50 border border-white/5 rounded-lg p-3 text-center" data-testid={`drip-stage-${s.key}`}>
                <p className={`text-xs font-mono font-bold ${s.color} mb-1`}>{s.label}</p>
                <p className="text-xl font-bold text-white">{status.pending_stages?.[s.key] ?? 0}</p>
                <p className="text-[10px] text-gray-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-3 flex items-baseline justify-between text-xs text-gray-400">
            <span>Łącznie w kolejce: <span className="text-white font-semibold">{status.pending_total}</span></span>
            <span>Wysłane łącznie: <span className="text-emerald-400 font-semibold">{status.total_sent_all_time}</span></span>
            {status.last_run_at && (
              <span>Ostatni tick: <span className="text-gray-300">{new Date(status.last_run_at).toLocaleString('pl-PL')}</span></span>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function ErrorLogsSection() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/errors/admin/recent?limit=30`, { headers: headers() });
      setErrors(data.errors || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); const t = setInterval(fetchLogs, 30000); return () => clearInterval(t); }, []);

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="error-logs-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-white text-base font-semibold mb-1">🐛 Błędy aplikacji (ostatnie 30)</h3>
          <p className="text-gray-500 text-xs">
            JS crashy + nieobsłużone promisy. Każdy ErrorBoundary zgłasza się tutaj. Auto-odświeża co 30s.
            {errors.length > 0 && <span className="text-red-400 ml-2">• {errors.length} w logu</span>}
          </p>
        </div>
        <button onClick={fetchLogs} disabled={loading} data-testid="errors-refresh-btn" className="px-3 py-1.5 bg-white/5 border border-white/10 text-gray-300 rounded-lg text-xs hover:bg-white/10">
          {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Odśwież'}
        </button>
      </div>

      {errors.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500 text-sm bg-emerald-500/5 rounded-lg border border-emerald-500/20">
          ✅ Brak błędów — czysto
        </div>
      )}

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {errors.map((e, i) => {
          const isOpen = expanded === i;
          const isProd = (e.url || '').includes('apisai.pl');
          return (
            <div key={i} className={`rounded-lg border overflow-hidden ${isProd ? 'border-red-500/30 bg-red-500/5' : 'border-white/10 bg-[#050510]/50'}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : i)}
                className="w-full text-left p-3 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-mono break-all">
                      {isProd && <span className="text-red-400 mr-1">🔴 PROD</span>}
                      {(e.message || '(no message)').slice(0, 160)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {e.url && <span className="text-emerald-400">{(e.url || '').split('?')[0].slice(0, 80)}</span>}
                      {e.timestamp && <span className="ml-2">{new Date(e.timestamp).toLocaleString('pl-PL')}</span>}
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-white/10 p-3 bg-black/30 text-xs text-gray-300 space-y-2">
                  {e.stack && (
                    <div>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Stack trace:</p>
                      <pre className="font-mono text-[11px] text-red-300 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{e.stack}</pre>
                    </div>
                  )}
                  {e.componentStack && (
                    <div>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Component stack:</p>
                      <pre className="font-mono text-[11px] text-amber-300 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">{e.componentStack}</pre>
                    </div>
                  )}
                  {e.userAgent && <p className="text-[11px] text-gray-500">UA: <span className="text-gray-400">{e.userAgent.slice(0, 120)}</span></p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



function SponsoredTogglesSection() {
  const [entityType, setEntityType] = useState('hodowla');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [list, setList] = useState([]);

  const refreshList = async () => {
    try {
      const res = await axios.get(`${API}/admin/sponsored/list`);
      setList(res.data.items || []);
    } catch { setList([]); }
  };

  useEffect(() => { refreshList(); }, []);

  const handleToggle = async (makeSponsored) => {
    if (!slug.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/toggle-sponsored`, null, {
        params: { entity_type: entityType, slug: slug.trim(), sponsored: makeSponsored },
      });
      setResult(res.data);
      await refreshList();
      if (res.data.ok) setSlug('');
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || 'Błąd sieci' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (type, removeSlug) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/toggle-sponsored`, null, {
        params: { entity_type: type, slug: removeSlug, sponsored: false },
      });
      await refreshList();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 bg-[#0c0c1a]/80 border border-white/10 rounded-xl p-5" data-testid="sponsored-toggle-section">
      <h3 className="text-white text-base font-semibold mb-1">⭐ Sponsorowane (żółta belka na górze katalogu)</h3>
      <p className="text-gray-500 text-xs mb-4">
        Oznacz hodowlę/miot/reproduktora jako sponsored. Będzie widoczna na górze w{' '}
        <code className="text-yellow-400">/katalog</code> i na stronach ras w sekcji „Sponsorowane" (max 3).
      </p>

      <div className="grid gap-3 md:grid-cols-[140px_1fr_auto_auto] items-end">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Typ</label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="w-full h-10 px-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white"
            data-testid="sponsored-type-select"
          >
            <option value="hodowla">Hodowla</option>
            <option value="miot">Miot</option>
            <option value="reproduktor">Reproduktor</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="np. apisai lub labrador-retriever-miot-a"
            className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-600"
            data-testid="sponsored-slug-input"
          />
        </div>
        <button
          onClick={() => handleToggle(true)}
          disabled={!slug.trim() || loading}
          data-testid="sponsored-add-btn"
          className="h-10 px-4 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black text-sm font-semibold disabled:opacity-40"
        >
          {loading ? '...' : '⭐ Oznacz'}
        </button>
        <button
          onClick={() => handleToggle(false)}
          disabled={!slug.trim() || loading}
          data-testid="sponsored-remove-btn"
          className="h-10 px-4 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 disabled:opacity-40"
        >
          Usuń
        </button>
      </div>

      {result && (
        <div className={`mt-3 p-3 rounded-lg text-xs ${result.ok ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'}`} data-testid="sponsored-result">
          {result.ok ? `✓ Zapisano (${result.modified} zmieniono)` : `Błąd: ${result.error}`}
        </div>
      )}

      <div className="mt-6" data-testid="sponsored-active-list">
        <h4 className="text-sm font-semibold text-white mb-2">
          Aktywne sponsorowane <span className="text-gray-500 font-normal">({list.length})</span>
        </h4>
        {list.length === 0 ? (
          <p className="text-xs text-gray-500">Brak aktywnych sponsorowanych. Dodaj pierwszą powyżej.</p>
        ) : (
          <div className="space-y-2">
            {list.map((item) => (
              <div key={`${item.type}-${item.slug}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium truncate">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-yellow-500/20 text-yellow-300 mr-2">{item.type}</span>
                    {item.name || item.slug}
                  </p>
                  <p className="text-xs text-gray-500">
                    slug: <code className="text-gray-400">{item.slug}</code>
                    {item.breed && ` · ${item.breed}`}
                    {item.city && ` · ${item.city}`}
                  </p>
                </div>
                <button
                  onClick={() => handleRemove(item.type, item.slug)}
                  disabled={loading}
                  data-testid={`sponsored-off-${item.slug}`}
                  className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs hover:bg-red-500/20"
                >
                  Wyłącz
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
